import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Button,
    Stack
} from "@mui/material";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

import { useUser } from "../components/UserContext";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { useApiClient } from "../components/useApiClient";
import useAuthToken from "../functions/useAuthToken";
import type { UserInviteAcceptance } from "../models/UserInvite";

const AcceptInvitePage: React.FC = () => {
    const { inviteId } = useParams<{ inviteId: string }>();
    const navigate = useNavigate();

    const { getAnnon, post } = useApiClient();
    const { invalidateUser } = useUser();
    const getAccessToken = useAuthToken();

    const isAuthenticated = useIsAuthenticated();
    const { instance } = useMsal();

    const [invite, setInvite] = useState<UserInviteAcceptance | null>(null);
    const [loading, setLoading] = useState(true);
    const [accepting, setAccepting] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const loadInvite = async () => {
            if (!inviteId) {
                setError("Invalid invite link.");
                setLoading(false);
                return;
            }

            try {
                const data = await getAnnon<UserInviteAcceptance>(
                    `invitations/${inviteId}`
                );

                setInvite(data);
            } catch (err) {
                console.error(err);
                setError("Invite is invalid or expired.");
            } finally {
                setLoading(false);
            }
        };

        void loadInvite();
    }, [inviteId]);

    const acceptInvite = async (id: string) => {
        try {
            setAccepting(true);
            setError("");

            const accessToken = await getAccessToken();

            if (!accessToken) {
                throw new Error("Unable to obtain an access token.");
            }

            await post(
                `invitations/${id}/accept`,
                accessToken,
                new FormData()
            );

            invalidateUser();
            setAccepted(true);

            setTimeout(() => {
                navigate("/");
            }, 2000);
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data ??
                err?.message ??
                "Error accepting invite."
            );
        } finally {
            setAccepting(false);
        }
    };

    useEffect(() => {
        if (!inviteId || !isAuthenticated) {
            return;
        }

        const pendingInviteId =
            sessionStorage.getItem("pendingInviteAccept");

        if (pendingInviteId !== inviteId) {
            return;
        }

        sessionStorage.removeItem("pendingInviteAccept");

        void acceptInvite(inviteId);
    }, [inviteId, isAuthenticated]);

    const handleAuthenticateAndAccept = async () => {
        if (!inviteId || !invite) {
            setError("Invalid invite link.");
            return;
        }

        try {
            setError("");

            if (isAuthenticated) {
                await acceptInvite(inviteId);
                return;
            }

            sessionStorage.setItem(
                "pendingInviteAccept",
                inviteId
            );

            if (invite.userExists) {
                await instance.loginRedirect({
                    ...loginRequest,
                    loginHint: invite.email,
                    state: JSON.stringify({
                        intent: "accept-invite",
                        inviteId
                    })
                });
            } else {
                await instance.loginRedirect({
                    ...loginRequest,
                    prompt: "select_account",
                    loginHint: invite.email,
                    state: JSON.stringify({
                        intent: "accept-invite",
                        inviteId
                    })
                });
            }
        } catch (err: any) {
            console.error(err);

            sessionStorage.removeItem("pendingInviteAccept");

            setError(
                err?.message ??
                "Unable to start the authentication process."
            );
        }
    };

    if (loading) {
        return (
            <CircularProgress
                sx={{
                    mt: 4,
                    mx: "auto",
                    display: "block"
                }}
            />
        );
    }

    return (
        <Box
            sx={{
                maxWidth: 500,
                mx: "auto",
                mt: 6,
                px: 2
            }}
        >
            <Paper sx={{ p: 3 }}>
                <Typography
                    variant="h5"
                    gutterBottom
                >
                    Accept Invitation
                </Typography>

                {error && (
                    <Typography
                        color="error"
                        sx={{ mb: 2 }}
                    >
                        {error}
                    </Typography>
                )}

                {accepted && (
                    <Typography color="primary">
                        Invitation accepted. Redirecting to your collection...
                    </Typography>
                )}

                {invite && !accepted && (
                    <Stack spacing={2}>
                        <Typography>
                            You've been invited to join{" "}
                            <strong>
                                {invite.clientName}
                            </strong>{" "}
                            as a{" "}
                            <strong>
                                {invite.roleName}
                            </strong>.
                        </Typography>

                        {invite.portfolioId && (
                            <Typography>
                                Your access will be specifically for the{" "}
                                <strong>
                                    {invite.portfolioName}
                                </strong>{" "}
                                portfolio.
                            </Typography>
                        )}

                        <Box>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Invitation sent to
                            </Typography>

                            <Typography fontWeight={600}>
                                {invite.email}
                            </Typography>
                        </Box>

                        {!isAuthenticated && !invite.userExists && (
                            <Alert severity="info" variant="outlined">
                                <AlertTitle>You'll need to create an account first</AlertTitle>
                                <Typography variant="body2">
                                    Clicking below takes you to Microsoft's sign-in page. Since
                                    you're new here, don't fill in the sign-in fields — instead,
                                    look for a{" "}
                                    <strong>"No account? Create one!"</strong>
                                    link further down the page, and use it to set up a new
                                    account with <strong>{invite.email}</strong>.
                                </Typography>
                                <Box
                                    component="img"
                                    src="/images/sign-up-page.png"
                                    alt="Microsoft sign-in page with the 'Create one!' link highlighted"
                                    sx={{ width: "100%", borderRadius: 1, display: "block" }}
                                />
                            </Alert>
                        )}

                        {!isAuthenticated && invite.userExists && (
                            <Alert severity="info" variant="outlined">
                                <Typography variant="body2">
                                    Clicking below takes you to Microsoft's sign-in page.
                                    Sign in with the account associated with{" "}
                                    <strong>{invite.email}</strong> to accept this invitation.
                                </Typography>
                                <Box
                                    component="img"
                                    src="/images/sign-in-page.png"
                                    alt="Microsoft sign-in page"
                                    sx={{ width: "100%", borderRadius: 1, display: "block" }}
                                />
                            </Alert>
                        )}

                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={accepting}
                            onClick={handleAuthenticateAndAccept}
                            sx={{ mt: 1 }}
                        >
                            {accepting
                                ? "Accepting invitation..."
                                : isAuthenticated
                                    ? "Accept Invitation"
                                    : invite.userExists
                                        ? "Sign in to accept invitation"
                                        : "Create account & accept invitation"}
                        </Button>
                    </Stack>
                )}
            </Paper>
        </Box>
    );
};

export default AcceptInvitePage;